<template>
  <b-form @submit.prevent="debouncedOnsubmitForm()">
    <div v-if="isLoading" class="py-5 text-center"><b-spinner /></div>
    <template v-if="!isLoading && passwordPolicy">
      <b-form-row>
        <b-form-group class="col-lg-6">
          <template v-slot:label>{{ $t("fullName") }} <span class="text-danger">*</span></template>         
          <b-form-input
            type="text"
            v-model="name"
            required
            minlength="2"
            maxlength="100"
            :pattern=fullNamePattern
            @invalid="$event.target.setCustomValidity($t('FormatInvalidName'))"
            @input.native="$event.target.setCustomValidity('')"
          />
        </b-form-group>
        <b-form-group class="col-lg-6">
          <template v-slot:label>{{ $t("phoneNumber") }} <span class="text-danger">*</span></template>
          <b-form-input type="tel" v-model.trim="phone" required :pattern="phonePattern" minlength="2" maxlength="50"
            @invalid="$event.target.setCustomValidity($t('invalidPhoneFormat'))"
            @input.native="$event.target.setCustomValidity('')" />
        </b-form-group>
      </b-form-row>
      <b-form-row>
        <b-form-group class="col-lg-6" :description="$t('usernameDescription')">
          <template v-slot:label>Username <span class="text-danger">*</span></template>
          <b-form-input type="text" v-model.trim="username" required minlength="2"
            maxlength="32" />
        </b-form-group>
        <b-form-group class="col-lg-6">
          <template v-slot:label>Email <span class="text-danger">*</span></template>
          <b-form-input type="email" v-model.trim="email" required maxlength="100"></b-form-input>
        </b-form-group>
      </b-form-row>
      <b-form-row>
        <b-form-group class="col-lg-6 mb-1">
          <template v-slot:label>{{ $t("password") }} <span class="text-danger">*</span></template>
          <b-input-group>
            <b-form-input v-model.trim="password" type="text" class="text-monospace custom-validity-fix" required autocomplete="off"
              :minlength="passwordPolicy.minPasswordLength" :maxlength="passwordPolicy.maxPasswordLength"
              :pattern="passwordPolicy.passwordPattern"
              @invalid="$event.target.setCustomValidity(passwordPolicy.passwordValidationFailedMessage)"
              @input.native="$event.target.setCustomValidity('')" />
            <b-input-group-append>
              <b-button class="btn-form" variant="outline-secondary" v-b-tooltip :title="$t('createRandomAccessCode')"
                @click="setRandomPassWord">
                <b-icon-arrow-repeat />
              </b-button>
            </b-input-group-append>
          </b-input-group>

        </b-form-group>
        <b-form-group class="col-lg-6 mb-1">
          <template v-slot:label>{{ $t("confirmPassword") }} <span class="text-danger">*</span></template>
          <b-form-input type="text" class="masked-password" autocomplete="off" v-model.trim="confirmPassword" required />
        </b-form-group>
      </b-form-row>
      <small class="form-text text-muted mb-3">{{ passwordPolicy.passwordDescription }}</small>
      <b-form-group>
        <template v-slot:label>{{ $t("address") }}</template>
        <b-form-textarea v-model="address" maxlength="255"></b-form-textarea>
      </b-form-group>

      <div v-if="formErrorMsg" class="text-danger" v-html="formErrorMsg"></div>

      <div class="text-right pt-3">
        <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">
          {{ $t("cancel") }}
        </b-button>
        <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("createAccount") }}</b-button>
      </div>
    </template>

  </b-form>
</template>

<style lang="scss" scoped></style>

<script lang="ts">
import { Component, Emit, Vue } from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";

import * as userService from "@/service/userService";
import * as validationUtils from "@/service/validationUtils";
import * as passwordPolicyService from "@/service/passwordPolicyService";
import {enumErrorType} from "@/views/enums/ErrorType";

import * as util from "@/service/util";
import { BounceTimes } from '@/views/enums/TimesNumbers';
@Component
export default class NewAdminAccountModal extends BaseComponent {

  isLoading = true;
  passwordPolicy = null;

  name = "";
  phone = "";
  username = "";
  email = "";
  password = "";
  confirmPassword = "";
  address = "";

  formErrorMsg = "";
  isSubmitting = false;

  // Emit decorators only
  @Emit("submitted") __submitted() { }


  mounted() {
    this.fetchPasswordPolicy();
  }

  async fetchPasswordPolicy() {
    try {
      this.isLoading = true;
      this.passwordPolicy = await passwordPolicyService.get();

    } catch (e) {
      this.toastError(e);

    } finally {
      this.isLoading = false;
    }
  }

  onClickCancel() {
    this.$bvModal.hide("new-account-modal");
  }

  debouncedOnsubmitForm = _.debounce(() => {
    this.submitForm ();
  }, BounceTimes.BOUNCETIMES);

  async submitForm() {
    this.formErrorMsg = "";
    this.name = this.name.trim();
    this.address = this.address.trim();
    if (!util.isValidUserName(this.username)) {

        this.formErrorMsg = "<b>"+this.username+"</b> "+ this.$t("includespacename");

        return;

    }

    if (!validationUtils.isEmail(this.email)) {
      this.formErrorMsg = this.$t("invalidEmail").toString();
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.formErrorMsg = this.$t("passwordsNotMatch").toString();
      return;
    }

    const data = {
      name: this.name,
      phone: this.phone,
      username: this.username.toLowerCase(),
      email: this.email.toLowerCase(),
      password: this.password,
      confirmPassword: this.confirmPassword,
      address: this.address
    };

    try {
      this.isSubmitting = true;
      await userService.createAdmin(data);

      this.$emit("submitted");
      this.toastSuccessDelay(this.$t("createAdminAccountSuccessful").toString());
      this.$bvModal.hide("new-account-modal");

    } catch (e) {
      this.formErrorMsg = this.showError(e, enumErrorType.MESSAGE);
    } finally {
      this.isSubmitting = false;
    }
  }
  setRandomPassWord() {
    this.password = util.randomPassword(10);
    this.confirmPassword = '';
    util.clearCustomValidity();
  }

}
</script>
